import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardBody, Container, Nav, NavItem, NavLink, Button, Input, Label, Row, Col } from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SearchableSelect from "../../../Components/Common/SearchableSelect";
import FilterCard from "../../../Components/Common/FilterCard";
import { DataTable, Column, TableAction } from "../../../Components/Common/DataTable";
import { getTodayDate, getDateMonthsAgo } from "../../../utils/dateUtils";
import { showModal } from "../../../hooks/useModal";
import {
  useCommunesQuery,
  useItiSurfaceTraiteeQuery,
  useDepartementsQuery,
  useDepSurfaceTraiteeQuery,
  useCumulItineraireQuery,
  useStatutAlertesQuery,
  invalidateExportationDataQueries,
  type ItiSurfaceTraiteePayload,
  type DepSurfaceTraiteePayload,
  type CumulItinerairePayload,
} from "../../../hooks/queries/useRoadAlertQueries";
import { exportExportationDataPdf } from "../../../utils/exportExportationDataPdf";
import { exportExportationDataExcel } from "../../../utils/exportExportationDataExcel";
import logoAgerouteLight from "../../../assets/images/logo-ageroute-light.png";

interface ExportationDataRow extends Record<string, any> {
  // Onglet 1 (surfaces-itineraire)
  itineraire?: string;
  dateitineraire?: string;
  surfacetraitee?: number;
  nbrepad?: number;
  nbrepcd?: number;
  communes?: string;
  libelledepartement?: string;
  idcommunes?: number;
  // Onglet 2 (surfaces-departement)
  departement?: string;
  iddepartement?: number;
  annee?: string;
  // Onglet 3 (cumul-itineraire)
  idCumulItineraire?: number;
  mois?: string;
  surfacePrevisionnel?: number;
  surfaceTraite?: number;
  resteSurface?: number;
  statut?: number;
}

const TABS = [
  { id: "surfaces-itineraire", label: "Surfaces traitées par Itinéraire" },
  { id: "surfaces-departement", label: "surfaces traitées par département" },
  { id: "cumul-itineraire", label: "Cumul itinéraire" },
];

const defaultFilters = {
  idCommune: "",
  idDepartement: "",
  dateDebut: getDateMonthsAgo(1),
  dateFin: getTodayDate(),
  annee: "",
  mois: "",
  statut: "",
};

const ExportationData = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("surfaces-itineraire");
  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<typeof defaultFilters | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoAgerouteLight) return;
    fetch(logoAgerouteLight)
      .then((r) => r.blob())
      .then((blob) => new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
      }))
      .then(setLogoDataUrl)
      .catch(() => {});
  }, []);

  const communesQuery = useCommunesQuery();
  const departementsQuery = useDepartementsQuery();
  const statutAlertesQuery = useStatutAlertesQuery();
  
  const communes = useMemo(() => {
    const d = communesQuery.data ?? [];
    return d.length > 0 ? d : [];
  }, [communesQuery.data]);
  
  const departements = useMemo(() => {
    const d = departementsQuery.data ?? [];
    return d.length > 0 ? d : [];
  }, [departementsQuery.data]);

  const statuts = useMemo(() => {
    const d = statutAlertesQuery.data ?? [];
    return d.length > 0 ? d : [];
  }, [statutAlertesQuery.data]);

  // Payload pour l'onglet 1 (surfaces-itineraire)
  const itiSurfaceTraiteePayload = useMemo((): ItiSurfaceTraiteePayload | null => {
    if (activeTab !== "surfaces-itineraire" || !appliedFilters) return null;
    return {
      data: {
        ...(appliedFilters.idCommune && { idCommune: appliedFilters.idCommune }),
        dateCreationParam: {
          operator: "[]",
          start: appliedFilters.dateDebut,
          end: appliedFilters.dateFin,
        },
      },
    };
  }, [activeTab, appliedFilters]);

  // Payload pour l'onglet 2 (surfaces-departement)
  const depSurfaceTraiteePayload = useMemo((): DepSurfaceTraiteePayload | null => {
    if (activeTab !== "surfaces-departement" || !appliedFilters) return null;
    return {
      data: {
        ...(appliedFilters.idDepartement && { idDepartement: appliedFilters.idDepartement }),
        dateCreationParam: {
          operator: "[]",
          start: appliedFilters.dateDebut,
          end: appliedFilters.dateFin,
        },
      },
    };
  }, [activeTab, appliedFilters]);

  // Payload pour l'onglet 3 (cumul-itineraire)
  const cumulItinerairePayload = useMemo((): CumulItinerairePayload | null => {
    if (activeTab !== "cumul-itineraire" || !appliedFilters) return null;
    return {
      data: {
        ...(appliedFilters.annee && { annee: appliedFilters.annee }),
        ...(appliedFilters.mois && { mois: appliedFilters.mois }),
        ...(appliedFilters.statut && { statut: appliedFilters.statut }),
      },
    };
  }, [activeTab, appliedFilters]);

  const itiSurfaceTraiteeQuery = useItiSurfaceTraiteeQuery(
    activeTab === "surfaces-itineraire" ? itiSurfaceTraiteePayload : null
  );
  
  const depSurfaceTraiteeQuery = useDepSurfaceTraiteeQuery(
    activeTab === "surfaces-departement" ? depSurfaceTraiteePayload : null
  );

  const cumulItineraireQuery = useCumulItineraireQuery(
    activeTab === "cumul-itineraire" ? cumulItinerairePayload : null
  );

  const data = useMemo(() => {
    if (activeTab === "surfaces-itineraire") {
      return (itiSurfaceTraiteeQuery.data ?? []) as ExportationDataRow[];
    }
    if (activeTab === "surfaces-departement") {
      return (depSurfaceTraiteeQuery.data ?? []) as ExportationDataRow[];
    }
    if (activeTab === "cumul-itineraire") {
      return (cumulItineraireQuery.data ?? []) as ExportationDataRow[];
    }
    return [];
  }, [activeTab, itiSurfaceTraiteeQuery.data, depSurfaceTraiteeQuery.data, cumulItineraireQuery.data]);

  const loading = activeTab === "surfaces-itineraire" 
    ? itiSurfaceTraiteeQuery.isLoading 
    : activeTab === "surfaces-departement"
    ? depSurfaceTraiteeQuery.isLoading
    : activeTab === "cumul-itineraire"
    ? cumulItineraireQuery.isLoading
    : false;

  useEffect(() => {
    setAppliedFilters(defaultFilters);
  }, []);

  // Réinitialiser les filtres lors du changement d'onglet
  useEffect(() => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  }, [activeTab]);

  const handleSearch = () => {
    invalidateExportationDataQueries(queryClient);
    setAppliedFilters(filters);
  };

  const handleExportPDF = () => {
    if (data.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }
    const periodDebut = appliedFilters?.dateDebut ?? filters.dateDebut;
    const periodFin = appliedFilters?.dateFin ?? filters.dateFin;
    const titles: Record<string, string> = {
      "surfaces-itineraire": "Rapport des surfaces traitées par Itinéraire",
      "surfaces-departement": "Rapport des surfaces traitées par Département",
      "cumul-itineraire": "Rapport cumul itinéraire",
    };
    const filenames: Record<string, string> = {
      "surfaces-itineraire": `rapport-surfaces-itineraire-${periodDebut}-${periodFin}.pdf`,
      "surfaces-departement": `rapport-surfaces-departement-${periodDebut}-${periodFin}.pdf`,
      "cumul-itineraire": `rapport-cumul-itineraire-${periodDebut}-${periodFin}.pdf`,
    };
    exportExportationDataPdf({
      data: data as any,
      dateDebut: periodDebut,
      dateFin: periodFin,
      filename: filenames[activeTab] || "rapport-exportation-data.pdf",
      title: titles[activeTab] || "Rapport d'exportation",
      logoDataUrl: logoDataUrl || undefined,
    });
  };

  const handleExportExcel = () => {
    if (data.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }
    const periodDebut = appliedFilters?.dateDebut ?? filters.dateDebut;
    const periodFin = appliedFilters?.dateFin ?? filters.dateFin;
    const titles: Record<string, string> = {
      "surfaces-itineraire": "Rapport des surfaces traitées par Itinéraire",
      "surfaces-departement": "Rapport des surfaces traitées par Département",
      "cumul-itineraire": "Rapport cumul itinéraire",
    };
    const filenames: Record<string, string> = {
      "surfaces-itineraire": `rapport-surfaces-itineraire-${periodDebut}-${periodFin}.xlsx`,
      "surfaces-departement": `rapport-surfaces-departement-${periodDebut}-${periodFin}.xlsx`,
      "cumul-itineraire": `rapport-cumul-itineraire-${periodDebut}-${periodFin}.xlsx`,
    };
    exportExportationDataExcel({
      data: data as any,
      dateDebut: periodDebut,
      dateFin: periodFin,
      filename: filenames[activeTab] || "rapport-exportation-data.xlsx",
      title: titles[activeTab] || "Rapport d'exportation",
      logoDataUrl: logoDataUrl || undefined,
    });
  };

  const handleShowDetail = useCallback((row: ExportationDataRow) => {
    const moisNames = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
    ];

    const formatDate = (dateStr?: string) => {
      if (!dateStr) return "-";
      try {
        if (dateStr.includes("-") && dateStr.length === 10) {
          const [y, m, d] = dateStr.split("-");
          return `${d}/${m}/${y}`;
        }
        return dateStr;
      } catch {
        return dateStr;
      }
    };

    const formatNumber = (value?: number | string) => {
      if (value == null) return "-";
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      if (isNaN(numValue)) return "-";
      return numValue.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    };

    const formatMois = (mois?: string) => {
      if (!mois) return "-";
      const moisNum = parseInt(String(mois), 10);
      if (!isNaN(moisNum) && moisNum >= 1 && moisNum <= 12) {
        return moisNames[moisNum - 1];
      }
      return mois;
    };

    let title = "Détails";
    let content: React.ReactNode;

    if (activeTab === "surfaces-itineraire") {
      title = "Détails - Surface traitée par Itinéraire";
      content = (
        <div className="p-3">
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Itinéraire</Label>
              <div className="text-gray-900">{row.itineraire ?? "-"}</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">Date</Label>
              <div className="text-gray-900">{formatDate(row.dateitineraire)}</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Surface traitée</Label>
              <div className="text-gray-900">{formatNumber(row.surfacetraitee)} m²</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">Commune</Label>
              <div className="text-gray-900">{row.communes ?? "-"}</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">PAD</Label>
              <div className="text-gray-900">{row.nbrepad != null ? row.nbrepad.toFixed(0) : "-"}</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">PCD</Label>
              <div className="text-gray-900">{row.nbrepcd != null ? row.nbrepcd.toFixed(0) : "-"}</div>
            </Col>
          </Row>
          {row.libelledepartement && (
            <Row>
              <Col md={6}>
                <Label className="fw-semibold text-muted">Département</Label>
                <div className="text-gray-900">{row.libelledepartement}</div>
              </Col>
            </Row>
          )}
        </div>
      );
    } else if (activeTab === "surfaces-departement") {
      title = "Détails - Surface traitée par Département";
      content = (
        <div className="p-3">
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Département</Label>
              <div className="text-gray-900">{row.departement ?? "-"}</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">Année</Label>
              <div className="text-gray-900">{row.annee ?? "-"}</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Surface traitée</Label>
              <div className="text-gray-900">{formatNumber(row.surfacetraitee)} m²</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">ID Département</Label>
              <div className="text-gray-900">{row.iddepartement != null ? `#${row.iddepartement}` : "-"}</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">PAD</Label>
              <div className="text-gray-900">{row.nbrepad != null ? row.nbrepad.toFixed(0) : "-"}</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">PCD</Label>
              <div className="text-gray-900">{row.nbrepcd != null ? row.nbrepcd.toFixed(0) : "-"}</div>
            </Col>
          </Row>
        </div>
      );
    } else if (activeTab === "cumul-itineraire") {
      title = "Détails - Cumul Itinéraire";
      content = (
        <div className="p-3">
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Itinéraire</Label>
              <div className="text-gray-900">{row.itineraire ?? "-"}</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">Année</Label>
              <div className="text-gray-900">{row.annee ?? "-"}</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Mois</Label>
              <div className="text-gray-900">{formatMois(row.mois)}</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">ID Cumul</Label>
              <div className="text-gray-900">{row.idCumulItineraire != null ? `#${row.idCumulItineraire}` : "-"}</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Surface prévisionnelle</Label>
              <div className="text-gray-900">{formatNumber(row.surfacePrevisionnel)} m²</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">Surface traitée</Label>
              <div className="text-gray-900">{formatNumber(row.surfaceTraite)} m²</div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Label className="fw-semibold text-muted">Reste surface</Label>
              <div className="text-gray-900">{formatNumber(row.resteSurface)} m²</div>
            </Col>
            <Col md={6}>
              <Label className="fw-semibold text-muted">Statut</Label>
              <div className="text-gray-900">{row.statut != null ? row.statut : "-"}</div>
            </Col>
          </Row>
        </div>
      );
    } else {
      content = <div className="p-3">Aucun détail disponible</div>;
    }

    // Différer l'ouverture de la modal pour éviter une boucle de re-renders
    const config = {
      title,
      content,
      size: "lg" as const,
      haveDefaultActionsButton: false,
      closeOnBackdropClick: true,
      // onCancel : appelé par le hook à la fermeture ; ne pas appeler hideModal() ici (évite une boucle de re-renders)
    };
    setTimeout(() => {
      showModal(config);
    }, 0);
  }, [activeTab]);

  // Configuration des colonnes selon l'onglet actif
  const columns: Column<ExportationDataRow>[] = useMemo(() => {
    if (activeTab === "surfaces-itineraire") {
      return [
        {
          key: "itineraire",
          label: "Itinéraire",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => (
            <span className="fw-semibold text-gray-900">
              {row?.itineraire ?? "-"}
            </span>
          ),
        },
        {
          key: "dateitineraire",
          label: "Date",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const date = row?.dateitineraire;
            if (!date) return <span className="text-gray-700">-</span>;
            // Formater la date si elle est au format YYYY-MM-DD
            try {
              if (date.includes("-") && date.length === 10) {
                const [y, m, d] = date.split("-");
                return <span className="text-gray-700">{`${d}/${m}/${y}`}</span>;
              }
              return <span className="text-gray-700">{date}</span>;
            } catch {
              return <span className="text-gray-700">{date}</span>;
            }
          },
        },
        {
          key: "surfacetraitee",
          label: "Surface traitée",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const surface = row?.surfacetraitee;
            if (surface == null) return <span className="text-gray-700">-</span>;
            const numValue = typeof surface === "string" ? parseFloat(surface) : surface;
            if (isNaN(numValue)) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {numValue.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²
              </span>
            );
          },
        },
        {
          key: "nbrepad",
          label: "PAD",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const pad = row?.nbrepad;
            if (pad == null) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {typeof pad === "number" ? pad.toFixed(0) : pad}
              </span>
            );
          },
        },
        {
          key: "nbrepcd",
          label: "PCD",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const pcd = row?.nbrepcd;
            if (pcd == null) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {typeof pcd === "number" ? pcd.toFixed(0) : pcd}
              </span>
            );
          },
        },
        {
          key: "communes",
          label: "Commune",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => (
            <span className="text-gray-700">
              {row?.communes ?? "-"}
            </span>
          ),
        },
      ];
    }
    if (activeTab === "surfaces-departement") {
      return [
        {
          key: "departement",
          label: "Département",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => (
            <span className="fw-semibold text-gray-900">
              {row?.departement ?? "-"}
            </span>
          ),
        },
        {
          key: "annee",
          label: "Année",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => (
            <span className="text-gray-700">
              {row?.annee ?? "-"}
            </span>
          ),
        },
        {
          key: "surfacetraitee",
          label: "Surface traitée",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const surface = row?.surfacetraitee;
            if (surface == null) return <span className="text-gray-700">-</span>;
            const numValue = typeof surface === "string" ? parseFloat(surface) : surface;
            if (isNaN(numValue)) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {numValue.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²
              </span>
            );
          },
        },
        {
          key: "nbrepad",
          label: "PAD",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const pad = row?.nbrepad;
            if (pad == null) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {typeof pad === "number" ? pad.toFixed(0) : pad}
              </span>
            );
          },
        },
        {
          key: "nbrepcd",
          label: "PCD",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const pcd = row?.nbrepcd;
            if (pcd == null) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {typeof pcd === "number" ? pcd.toFixed(0) : pcd}
              </span>
            );
          },
        },
      ];
    }
    if (activeTab === "cumul-itineraire") {
      return [
        {
          key: "itineraire",
          label: "Itinéraire",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => (
            <span className="fw-semibold text-gray-900">
              {row?.itineraire ?? "-"}
            </span>
          ),
        },
        {
          key: "annee",
          label: "Année",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => (
            <span className="text-gray-700">
              {row?.annee ?? "-"}
            </span>
          ),
        },
        {
          key: "mois",
          label: "Mois",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const mois = row?.mois;
            if (!mois) return <span className="text-gray-700">-</span>;
            const moisNames = [
              "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
              "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
            ];
            const moisNum = parseInt(String(mois), 10);
            if (!isNaN(moisNum) && moisNum >= 1 && moisNum <= 12) {
              return <span className="text-gray-700">{moisNames[moisNum - 1]}</span>;
            }
            return <span className="text-gray-700">{mois}</span>;
          },
        },
        {
          key: "surfacePrevisionnel",
          label: "Surface prévisionnelle",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const surface = row?.surfacePrevisionnel;
            if (surface == null) return <span className="text-gray-700">-</span>;
            const numValue = typeof surface === "string" ? parseFloat(surface) : surface;
            if (isNaN(numValue)) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {numValue.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²
              </span>
            );
          },
        },
        {
          key: "surfaceTraite",
          label: "Surface traitée",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const surface = row?.surfaceTraite;
            if (surface == null) return <span className="text-gray-700">-</span>;
            const numValue = typeof surface === "string" ? parseFloat(surface) : surface;
            if (isNaN(numValue)) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {numValue.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²
              </span>
            );
          },
        },
        {
          key: "resteSurface",
          label: "Reste surface",
          sortable: true,
          visible: true,
          render: (_: unknown, row?: ExportationDataRow) => {
            const surface = row?.resteSurface;
            if (surface == null) return <span className="text-gray-700">-</span>;
            const numValue = typeof surface === "string" ? parseFloat(surface) : surface;
            if (isNaN(numValue)) return <span className="text-gray-700">-</span>;
            return (
              <span className="text-gray-700">
                {numValue.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m²
              </span>
            );
          },
        },
      ];
    }
    return [];
  }, [activeTab]);

  // Actions pour chaque ligne (bouton Détail)
  const actions: TableAction<ExportationDataRow>[] = useMemo(() => [
    {
      label: "Détail",
      icon: "ri-eye-line",
      onClick: (row) => {
        if (row) {
          handleShowDetail(row);
        }
      },
      variant: "primary",
    },
  ], [handleShowDetail]);

  // Options pour les selects searchable
  const communeOptions = useMemo(() => [
    { value: "", label: "-Tous-" },
    ...((Array.isArray(communes) ? communes : []) as Record<string, unknown>[]).map((commune) => ({
      value: String(commune.idCommune ?? commune.id ?? ""),
      label: String(commune.libelleCommune ?? commune.libelle ?? "-"),
    })),
  ], [communes]);

  const departementOptions = useMemo(() => {
    const list = Array.isArray(departements) ? departements : [];
    const idKey = list.length && list[0]?.idDepartement != null ? "idDepartement" : "id";
    const libKey = list.length && list[0]?.libelleDepartement != null ? "libelleDepartement" : list[0]?.libelle != null ? "libelle" : "label";
    return [
      { value: "", label: "-Tous-" },
      ...list.map((dep: Record<string, unknown>) => ({
        value: String(dep[idKey] ?? ""),
        label: String(dep[libKey] ?? dep[idKey] ?? "-"),
      })),
    ];
  }, [departements]);

  // Options pour année (générer les 5 dernières années)
  const anneeOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      years.push({ value: String(year), label: String(year) });
    }
    return [{ value: "", label: "-Tous-" }, ...years];
  }, []);

  // Options pour mois
  const moisOptions = useMemo(() => {
    const moisNames = [
      { value: "01", label: "Janvier" },
      { value: "02", label: "Février" },
      { value: "03", label: "Mars" },
      { value: "04", label: "Avril" },
      { value: "05", label: "Mai" },
      { value: "06", label: "Juin" },
      { value: "07", label: "Juillet" },
      { value: "08", label: "Août" },
      { value: "09", label: "Septembre" },
      { value: "10", label: "Octobre" },
      { value: "11", label: "Novembre" },
      { value: "12", label: "Décembre" },
    ];
    return [{ value: "", label: "-Tous-" }, ...moisNames];
  }, []);

  // Options pour statut
  const statutOptions = useMemo(() => {
    const list = (Array.isArray(statuts) ? statuts : []) as Record<string, unknown>[];
    const first = list[0];
    const idKey = list.length && first?.idStatutAlerte != null ? "idStatutAlerte" : first?.id != null ? "id" : "value";
    const libKey = list.length && first?.libelleStatutAlerte != null ? "libelleStatutAlerte" : first?.libelle != null ? "libelle" : "label";
    return [
      { value: "", label: "-Tous-" },
      ...list.map((statut) => ({
        value: String(statut[idKey] ?? ""),
        label: String(statut[libKey] ?? statut[idKey] ?? "-"),
      })),
    ];
  }, [statuts]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Exportation Data" pageTitle="Exportation Data" />
          
          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden mb-4">
            <CardBody>
              {/* Navigation par onglets */}
              <Nav tabs className="modern-tabs mb-4">
                {TABS.map((tab) => (
                  <NavItem key={tab.id}>
                    <NavLink
                      style={{ cursor: "pointer" }}
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </CardBody>
          </Card>

          {/* Filtres */}
          <FilterCard title="Filtres de recherche">
            <Row className="mb-0">
                {activeTab === "cumul-itineraire" ? (
                  <>
                    <Col md={3}>
                      <Label className="form-label">Année</Label>
                      <SearchableSelect
                        options={anneeOptions}
                        value={filters.annee || null}
                        onChange={(value) => {
                          const newValue = value !== null && value !== undefined ? String(value) : "";
                          setFilters({ ...filters, annee: newValue });
                        }}
                        placeholder="-Tous-"
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="form-label">Mois</Label>
                      <SearchableSelect
                        options={moisOptions}
                        value={filters.mois || null}
                        onChange={(value) => {
                          const newValue = value !== null && value !== undefined ? String(value) : "";
                          setFilters({ ...filters, mois: newValue });
                        }}
                        placeholder="-Tous-"
                      />
                    </Col>
                    <Col md={3}>
                      <Label className="form-label">Statut</Label>
                      <SearchableSelect
                        options={statutOptions}
                        value={filters.statut || null}
                        onChange={(value) => {
                          const newValue = value !== null && value !== undefined ? String(value) : "";
                          setFilters({ ...filters, statut: newValue });
                        }}
                        placeholder="-Tous-"
                      />
                    </Col>
                    <Col md={3} className="d-flex align-items-end gap-2 flex-wrap">
                      <Button className="rounded-pill" color="secondary" onClick={handleSearch}>
                        <i className="ri-search-line me-1" />Rechercher
                      </Button>
                      <Button className="rounded-pill btn-ageroute-orange shadow-sm" onClick={handleExportPDF}>
                        <i className="ri-file-pdf-line me-1" />PDF
                      </Button>
                      <Button className="rounded-pill btn-ageroute-orange shadow-sm" onClick={handleExportExcel}>
                        <i className="ri-file-excel-2-line me-1" />Excel
                      </Button>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md={3}>
                      {activeTab === "surfaces-itineraire" ? (
                        <>
                          <Label className="form-label">Commune</Label>
                          <SearchableSelect
                            options={communeOptions}
                            value={filters.idCommune || null}
                            onChange={(value) => {
                              const newValue = value !== null && value !== undefined ? String(value) : "";
                              setFilters({ ...filters, idCommune: newValue });
                            }}
                            placeholder="-Tous-"
                          />
                        </>
                      ) : (
                        <>
                          <Label className="form-label">Département</Label>
                          <SearchableSelect
                            options={departementOptions}
                            value={filters.idDepartement || null}
                            onChange={(value) => {
                              const newValue = value !== null && value !== undefined ? String(value) : "";
                              setFilters({ ...filters, idDepartement: newValue });
                            }}
                            placeholder="-Tous-"
                          />
                        </>
                      )}
                    </Col>
                    <Col md={3}>
                      <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date début</Label>
                      <Input type="date" className="form-control rounded-3" value={filters.dateDebut} onChange={(e) => setFilters({ ...filters, dateDebut: e.target.value })} />
                    </Col>
                    <Col md={3}>
                      <Label className="small text-muted mb-1"><i className="ri-calendar-line me-1" />Date fin</Label>
                      <Input type="date" className="form-control rounded-3" value={filters.dateFin} onChange={(e) => setFilters({ ...filters, dateFin: e.target.value })} />
                    </Col>
                    <Col md={3} className="d-flex align-items-end gap-2 flex-wrap">
                      <Button className="rounded-pill" color="secondary" onClick={handleSearch}>
                        <i className="ri-search-line me-1" />Rechercher
                      </Button>
                      <Button className="rounded-pill btn-ageroute-orange shadow-sm" onClick={handleExportPDF}>
                        <i className="ri-file-pdf-line me-1" />PDF
                      </Button>
                      <Button className="rounded-pill btn-ageroute-orange shadow-sm" onClick={handleExportExcel}>
                        <i className="ri-file-excel-2-line me-1" />Excel
                      </Button>
                    </Col>
                  </>
                )}
            </Row>
          </FilterCard>

          {/* Tableau de données */}
          <Card className="card-ageroute rounded-3 shadow-sm border-0 overflow-hidden">
            <CardBody className="p-3 p-md-4">
              <DataTable
                instanceId="exportation-data-table"
                data={data}
                columns={columns}
                actions={actions}
                isLoading={loading}
                pagination={true}
                pageSize={10}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ExportationData;
